import type {
  CardPaymentEvents,
  CartFulfillEvents,
  CartQuoteEvents,
  ContactEvents,
  PassengerEvents,
} from "@hopper-b2b/checkout";
import type { ProductType } from "@hopper-b2b/types";
import type { RewardsPaymentEvents } from "@hsbc/rewards";
import type { WalletPaymentEvent } from "@hsbc/wallet";

export enum Event {
  // CommonEvents
  NEXT = "NEXT",
  PREVIOUS = "PREVIOUS",
  CHANGE = "CHANGE",
  GO_IDLE = "GO_IDLE",

  OPEN_PASSPORT_FORM = "OPEN_PASSPORT_FORM",
  CLEAR_CURRENT_INFANT_ID = "CLEAR_CURRENT_INFANT_ID",
  ON_INFANT_MODAL_CONTINUE = "ON_INFANT_MODAL_CONTINUE",

  // ContactInformationEvents
  SET_CONTACT_INFO = "SET_CONTACT_INFO",
  CLEAR_CONTACT_ERROR = "CLEAR_CONTACT_ERROR",

  // ScheduleQuote
  CLEAR_SCHEDULE_QUOTE_ERROR = "CLEAR_SCHEDULE_QUOTE_ERROR",

  // PaymentInformation
  OPEN_PAYMENT_FORM = "OPEN_PAYMENT_FORM",
  CLOSE_PAYMENT_FORM = "CLOSE_PAYMENT_FORM",
  SET_APPLIED_BALANCE = "SET_APPLIED_BALANCE",
  VERIFY_PAYMENT_METHOD = "VERIFY_PAYMENT_METHOD",
  DELETE_PAYMENT_METHOD = "DELETE_PAYMENT_METHOD",
  SET_PAYMENT_METHOD_ID = "SET_PAYMENT_METHOD_ID",
  SET_SELECTED_PAYMENT_METHOD_ID = "SET_SELECTED_PAYMENT_METHOD_ID",
  CLEAR_PAYMENT_ERROR = "CLEAR_PAYMENT_ERROR",

  // Review
  GO_TO_TRAVELER_SELECT = "GO_TO_TRAVELER_SELECT",
  GO_TO_CONTACT_INFORMATION = "GO_TO_CONTACT_INFORMATION",
  EDIT_SEATS_FROM_REVIEW = "EDIT_SEATS_FROM_REVIEW",

  // ScheduleFulfill
  CLEAR_SCHEDULE_FULFILL_ERROR = "CLEAR_SCHEDULE_FULFILL_ERROR",

  // Confirmation
  GO_HOME = "GO_HOME",

  // Transition to specific state
  GO_TO_CART_QUOTE = "GO_TO_CART_QUOTE",
  GO_TO_CART_FULFILL = "GO_TO_CART_FULFILL",
  GO_TO_SEAT_SELECTION = "GO_TO_SEAT_SELECTION",
  GO_TO_PAYMENT = "GO_TO_PAYMENT",
  GO_TO_REVIEW = "GO_TO_REVIEW",
  GO_TO_BOOKING_CONFIRMATION = "GO_TO_BOOKING_CONFIRMATION",
}

export const CheckoutEvent = {
  ...Event,
} as const;

export type CheckoutEvent = (typeof CheckoutEvent)[keyof typeof CheckoutEvent];

export interface GoToTravelerSelect {
  type: Event.GO_TO_TRAVELER_SELECT;
}

export interface GoToContactInformation {
  type: Event.GO_TO_CONTACT_INFORMATION;
}

export interface GoToPayment {
  type: Event.GO_TO_PAYMENT;
}
export interface GoToReview {
  type: Event.GO_TO_REVIEW;
}
export interface GoToCartQuote {
  type: Event.GO_TO_CART_QUOTE;
}
export interface GoToCartFulfill {
  type: Event.GO_TO_CART_FULFILL;
}
export interface GoToBookingConfirmation {
  type: Event.GO_TO_BOOKING_CONFIRMATION;
}
export interface NextEvent {
  type: Event.NEXT;
  productType?: ProductType;
}
export interface PreviousEvent {
  type: Event.PREVIOUS;
}
export interface ClearPaymentError {
  type: Event.CLEAR_PAYMENT_ERROR;
}

export interface SetSelectedPaymentMethodIdEvent {
  type: Event.SET_SELECTED_PAYMENT_METHOD_ID;
  paymentMethod: {
    paymentId: string | undefined;
  };
}
export interface VerifyPaymentMethodEvent {
  type: Event.VERIFY_PAYMENT_METHOD;
  token: string;
}

export interface DeletePaymentMethodEvent {
  type: Event.DELETE_PAYMENT_METHOD;
  paymentMethod: {
    paymentId: string;
  };
}

export interface ClosePaymentFormEvent {
  type: Event.CLOSE_PAYMENT_FORM;
}

export interface OpenPaymentFormEvent {
  type: Event.OPEN_PAYMENT_FORM;
}
export type TEvent =
  | NextEvent
  | PreviousEvent
  | GoToTravelerSelect
  | GoToContactInformation
  | GoToPayment
  | GoToReview
  | GoToCartFulfill
  | GoToCartQuote
  | GoToBookingConfirmation
  | ContactEvents
  | PassengerEvents
  | CartQuoteEvents
  | CartFulfillEvents
  | ClearPaymentError
  | SetSelectedPaymentMethodIdEvent
  | VerifyPaymentMethodEvent
  | DeletePaymentMethodEvent
  | ClosePaymentFormEvent
  | OpenPaymentFormEvent
  // Card Payment Events
  | CardPaymentEvents
  // Rewards Events
  | RewardsPaymentEvents
  // Wallet Events
  | WalletPaymentEvent;
